import React, { useCallback } from 'react';

import {
  AutocompleteOptionsDropdown,
  GooglePrediction,
  ITextInputProps,
} from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import useLocationPredictions from 'hooks/geolocation/use-location-predictions';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

type BaseInputProps = Pick<ITextInputProps, 'value' | 'className'>;

export interface IAutocompleteAddressInputProps extends BaseInputProps {
  'data-testid': string;
  autoFocus?: boolean;
  label: string;
  matchTypes?: ('address' | '(region)' | 'geocode' | 'establishment' | '(cities)')[];
  required: boolean;

  message?: string;
  setMessage?: React.Dispatch<React.SetStateAction<string>>;

  onAddressSelected: (address: string, placeId?: string) => void;
  onInputValueChange: React.Dispatch<React.SetStateAction<string>>;
}

export const AutocompleteAddressInput = React.forwardRef<
  HTMLInputElement,
  IAutocompleteAddressInputProps
>(
  (
    {
      'data-testid': testId,
      label,
      matchTypes = [],
      message,
      onAddressSelected = () => {},
      onInputValueChange,
      setMessage,
      value,
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const mParticle = useMParticleContext();

    /**
     * Retrieve the predictions based on the user's entry and map them to the format that the component prop expects.
     * Only pass on the first 5 predictions.
     */
    const predictions: GooglePrediction[] = useLocationPredictions(value, {
      includeExactMatch: true,
      matchTypes,
    })
      ?.slice(0, 5)
      ?.map(prediction => ({
        id: prediction.place_id,
        description: prediction.description,
        mainText: prediction.structured_formatting.main_text,
        secondaryText: prediction.structured_formatting.secondary_text,
      }));

    const handleDropdownValueSelected = useCallback(
      ({ address, placeId }) => {
        onAddressSelected(address, placeId);
        mParticle.logEvent(CustomEventNames.MANUAL_LOCATION_SEARCH, EventTypes.Search);
      },
      [mParticle, onAddressSelected]
    );

    const handleInputValueChange = useCallback(
      (inputValue: string | undefined) => {
        onInputValueChange(inputValue || '');

        if (setMessage) {
          setMessage('');
        }
      },
      [setMessage, onInputValueChange]
    );

    return (
      <>
        <AutocompleteOptionsDropdown
          noPredictionsFoundText={formatMessage({ id: 'noLocationsMatchYourSearch' })}
          data-testid={testId}
          onSelection={handleDropdownValueSelected}
          predictions={predictions}
          onInputChange={handleInputValueChange}
          value={value}
          label={label}
          errorMessage={message}
          ref={ref}
          clearTextIconTitle={formatMessage({ id: 'clearText' })}
        />
      </>
    );
  }
);

export default AutocompleteAddressInput;
